// Shared YouTube thumbnail resolver for roster live-video cells across the
// Live Content, Creator Content, and Finalized tabs. Centralizes precedence
// so each tab call site is one line.
//
// Background: the backend writes `live_video.thumbnail_url` only for Instagram
// (Apify CDN URLs aren't stable, so the IG cron uploads to S3 and stores the
// public URL). For YouTube the BE intentionally leaves `thumbnail_url` empty —
// the FE is expected to derive it from the 11-char video id, which YT serves
// off a stable CDN at `https://i.ytimg.com/vi/<id>/hqdefault.jpg`.
//
// Each row provides up to three signals; precedence below.

const YT_CDN_BASE = 'https://i.ytimg.com/vi';
const YT_ID_REGEX = /^[A-Za-z0-9_-]{11}$/;

// Structural input — wider than @discovr/types `LiveVideo` because the wire
// field is `locator` while the type today calls it `video_id`. Both are read;
// whichever is present wins. Resolves automatically if the type is renamed
// later.
export type LiveVideoForThumb = {
  thumbnail_url?: string | null;
  url?: string | null;
  locator?: string | null;
  video_id?: string | null;
  platform?: string | null;
};

/**
 * Extracts an 11-char YouTube video id from any common YouTube URL form,
 * or returns the input unchanged when it's already a bare id.
 *
 * Handles: youtube.com/watch?v=ID, youtube.com/shorts/ID, youtube.com/embed/ID,
 * youtube.com/live/ID, youtu.be/ID, and a bare 11-char id passed directly.
 *
 * Returns null for anything else, including non-YouTube hosts and malformed
 * paths. Safe to call with null/undefined.
 */
export function parseYouTubeVideoId(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Bare id passed in (canonical case — from live_video.locator).
  if (YT_ID_REGEX.test(trimmed)) return trimmed;

  try {
    const u = new URL(trimmed);
    const host = u.hostname.replace(/^www\./, '').replace(/^m\./, '');

    if (host === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '').split('/')[0] ?? '';
      return YT_ID_REGEX.test(id) ? id : null;
    }

    if (host === 'youtube.com' || host.endsWith('.youtube.com')) {
      const v = u.searchParams.get('v');
      if (v && YT_ID_REGEX.test(v)) return v;

      const shorts = u.pathname.match(/\/shorts\/([^/?]+)/);
      if (shorts?.[1] && YT_ID_REGEX.test(shorts[1])) return shorts[1];

      const embed = u.pathname.match(/\/embed\/([^/?]+)/);
      if (embed?.[1] && YT_ID_REGEX.test(embed[1])) return embed[1];

      // Livestream permalink — youtube.com/live/<id>.
      const live = u.pathname.match(/\/live\/([^/?]+)/);
      if (live?.[1] && YT_ID_REGEX.test(live[1])) return live[1];

      const vPath = u.pathname.match(/\/v\/([^/?]+)/);
      if (vPath?.[1] && YT_ID_REGEX.test(vPath[1])) return vPath[1];
    }
  } catch {
    // Fall through to the last-ditch regex scan below.
  }

  // Defensive: input wasn't a valid URL but might still contain an id (e.g.
  // user-pasted "watch?v=hy3yQmI0q4g" without the scheme).
  const m = trimmed.match(/(?:v=|\/shorts\/|\/embed\/|\/live\/|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m?.[1] ?? null;
}

/**
 * Best-effort still image for a YouTube URL (or bare id): the stable
 * `hqdefault.jpg` off YT's CDN. Returns null for non-YouTube / unparseable
 * input. One home for the CDN path so reference cards don't hardcode it.
 */
export function youTubeThumbnailUrl(url: string | null | undefined): string | null {
  const id = parseYouTubeVideoId(url);
  return id ? `${YT_CDN_BASE}/${id}/hqdefault.jpg` : null;
}

/**
 * Resolves the best thumbnail URL for a roster live-video cell.
 *
 * Precedence:
 *   1. live_video.thumbnail_url (IG path — BE writes Apify-S3 URL here).
 *   2. live_video.locator or live_video.video_id (YT canonical id from BE).
 *   3. parsed video id from live_video.url (YT URL fallback).
 *   4. null.
 *
 * Returns null when no thumbnail can be resolved (e.g. live_pending row with
 * no URL yet, or an IG row whose thumbnail_url hasn't been refreshed). Caller
 * should fall back to a placeholder UI in that case.
 */
export function liveVideoThumbnailUrl(
  liveVideo: LiveVideoForThumb | null | undefined,
): string | null {
  if (!liveVideo) return null;

  const directThumb = liveVideo.thumbnail_url?.trim();
  if (directThumb) return directThumb;

  const id =
    parseYouTubeVideoId(liveVideo.locator) ??
    parseYouTubeVideoId(liveVideo.video_id) ??
    parseYouTubeVideoId(liveVideo.url);

  return id ? `${YT_CDN_BASE}/${id}/hqdefault.jpg` : null;
}
